import React from 'react'
import { Box, Flex } from '@chakra-ui/core'
import { css } from '@emotion/core'
import { ThumbsDown, ThumbsUp } from 'lucide-react'

import keys from 'constants/keys'
import { TASK_STATUS_ID_BY_VALUE, TaskStatusId } from 'utils/taskStatusModel'

import { SkeletonBlock } from './common'
import { contextStatusMeta } from './meta'
import { ContextRow } from './useTaskDashboardData'
import LocaleText from 'components/LocaleText'
import useLocaleText from 'components/useLocaleText'
import { HorizontalScroller } from 'components/ui'

/**
 * The 5 task statuses (`utils/taskStatusModel`). Approved-with-reservation and AI-reviewed are
 * NOT filters here — they fold into `approved`; their counts show up in the per-row breakdown
 * block (`StatusComposition`) instead.
 */
export type StatusFilterId = TaskStatusId

export type StatusChipDef = {
  id: StatusFilterId
  value: number
  meta: { label: string; text: string; bar: string; bg: string }
}

const StatusCountChip: React.FC<{
  label: string
  value: number
  color: string
  isActive: boolean
  prefix?: React.ReactNode
  onClick: VoidFunction
}> = ({ label, value, color, isActive, prefix, onClick }) => (
  <Flex
    as="button"
    aria-pressed={isActive}
    align="center"
    flexShrink={0}
    bg="transparent"
    border="none"
    borderBottom="2px solid"
    borderBottomColor={isActive ? color : 'transparent'}
    borderRadius="0"
    px="0"
    py="6px"
    color={color}
    fontSize="10px"
    fontWeight="700"
    lineHeight="1.2"
    cursor="pointer"
    boxShadow="none"
    transition="all 0.15s ease-out"
    onClick={onClick}
    css={css`
      &:hover {
        color: ${color};
        border-bottom-color: ${color};
      }
      &:focus {
        outline: none;
        box-shadow: none;
      }
    `}
    style={{ gap: '4px' }}
  >
    {prefix}
    <span>
      <LocaleText text={label} />
    </span>
    <span>{value}</span>
  </Flex>
)

const StatusTabsSkeleton = () => (
  <>
    {[42, 78, 72, 64, 72, 68].map((w, i) => (
      <Flex key={i} py="6px" align="center">
        <SkeletonBlock h="10px" w={`${w}px`} borderRadius="3px" />
      </Flex>
    ))}
  </>
)

interface ITaskPreviewStatusTabsProps {
  isLoading: boolean
  contexts: ContextRow[]
  statusChips: StatusChipDef[]
  selectedStatusFilter: StatusFilterId | null
  onSelectFilter: (id: StatusFilterId | null) => void
  rightSlot?: React.ReactNode
}

export const TaskPreviewStatusTabs: React.FC<ITaskPreviewStatusTabsProps> = ({
  isLoading,
  contexts,
  statusChips,
  selectedStatusFilter,
  onSelectFilter,
  rightSlot,
}) => {
  const tAllTasks = useLocaleText('assigned_by_all_filter')
  return (
    <Flex mt="8px" align="center" justify="space-between" style={{ gap: '8px' }}>
      <HorizontalScroller gap="14px">
        {isLoading ? (
          <StatusTabsSkeleton />
        ) : (
          [
            <StatusCountChip
              key="all"
              label={tAllTasks}
              value={contexts.length}
              color="#334155"
              isActive={selectedStatusFilter === null}
              prefix={<Box w="6px" h="6px" borderRadius="50%" bg="#334155" flexShrink={0} />}
              onClick={() => onSelectFilter(null)}
            />,
            ...statusChips.map(({ id, value, meta }) => (
              <StatusCountChip
                key={id}
                label={meta.label}
                value={value}
                color={meta.bar}
                isActive={selectedStatusFilter === id}
                prefix={
                  id === 'approved' ? (
                    <ThumbsUp size={9} />
                  ) : id === 'rejected' ? (
                    <ThumbsDown size={9} />
                  ) : (
                    <Box w="6px" h="6px" borderRadius="50%" bg={meta.bar} flexShrink={0} />
                  )
                }
                onClick={() => onSelectFilter(selectedStatusFilter === id ? null : id)}
              />
            )),
          ]
        )}
      </HorizontalScroller>
      {rightSlot && <Box flexShrink={0}>{rightSlot}</Box>}
    </Flex>
  )
}

export const buildStatusChips = (
  contexts: ContextRow[],
  opts: { isAiTask?: boolean; isVmTask?: boolean } = {}
): StatusChipDef[] => {
  const { isAiTask = false, isVmTask = false } = opts
  const counts = contexts.reduce<Record<StatusFilterId, number>>(
    (acc, context) => {
      const id = TASK_STATUS_ID_BY_VALUE[context.status]
      if (id) acc[id] += 1
      return acc
    },
    { todo: 0, inProgress: 0, done: 0, approved: 0, rejected: 0 }
  )

  const chips: StatusChipDef[] = [
    { id: 'todo', value: counts.todo, meta: contextStatusMeta[keys.TASK_STATUSES.TODO.value] },
    { id: 'inProgress', value: counts.inProgress, meta: contextStatusMeta[keys.TASK_STATUSES.INPROGRESS.value] },
    { id: 'done', value: counts.done, meta: contextStatusMeta[keys.TASK_STATUSES.DONE.value] },
  ]

  // Approved/rejected are only reachable through a review loop: always shown for VM tasks (a 0
  // is meaningful there) and otherwise only when contexts actually reached that status — an AI
  // task whose subtasks are all AI-reviewed resolves to `approved`.
  if (isVmTask || isAiTask || counts.approved > 0) {
    chips.push({ id: 'approved', value: counts.approved, meta: contextStatusMeta[keys.TASK_STATUSES.APPROVED.value] })
  }
  if (isVmTask || counts.rejected > 0) {
    chips.push({
      id: 'rejected',
      value: counts.rejected,
      meta: contextStatusMeta[keys.TASK_STATUSES.DISAPPROVED.value],
    })
  }

  return chips
}
